import TransactionModal from "./transactionModal.js";
import { $_GET, trans } from "../globalFunctions.js";

export default class AddPlanModal extends TransactionModal {
	constructor(companyId) {
		super("add-plan-modal", companyId);

		this.VESTING_TYPE_SIMPLE = 1;
		this.formFields = [
			{
				name: "id",
				varType: "int",
			},
			{
				name: "name",
				varType: "string",
				required: true,
			},
			{
				name: "pool",
				varType: "int",
				required: true,
			},
			{
				name: "date",
				varType: "string",
				required: true,
			},
			{
				name: "grant_type",
				varType: "int",
				trigger: "change",
				required: true,
			},
			{
				name: "documents",
				varType: "simple-array",
				type: "int",
			},
			{
				name: "notes",
			},
			{
				name: "vesting_type",
				varType: "int",
				trigger: "change",
			},
			{
				name: "vesting_duration",
				varType: "int",
			},
			{
				name: "vesting_interval",
				varType: "int",
			},
			{
				name: "vesting_cliff",
				varType: "int",
			},
			{
				name: "good_leaver",
				varType: "string",
			},
			{
				name: "bad_leaver",
				varType: "string",
			},
			{
				name: "liquidity_event",
				varType: "string",
			},
			{
				name: "strike_price",
				varType: "float",
			},
			{
				name: "purchase_price",
				varType: "float",
			},
			{
				name: "voluntary",
				varType: "int",
			},
			{
				name: "involuntary",
				varType: "int",
			},
			{
				name: "with_cause",
				varType: "int",
			},
			{
				name: "death",
				varType: "int",
			},
			{
				name: "disability",
				varType: "int",
			},
			{
				name: "retirement",
				varType: "int",
			},
		];

		this.$vestingType = this.$form.querySelector("[name=\"vesting-type\"]");

		this.setupForm();

		Promise.all([this.documentsPromise, this.setupPools()]).then(() => {
			this.whenReady();
		});
	}

	whenReady() {
		super.whenReady();

		if (this.currentData === null && this.prefillData === null) {
			this.$vestingType.value = 1;
			this.getField("vesting-cliff").value = 12;
			this.getField("vesting-duration").value = 48;
			this.getField("vesting-interval").value = 1;
		}
		this.$vestingType.dispatchEvent(new Event("change"));

		this.checkIfHasPools();
		this.showOrHideElements(!this.isNew());
		this.handleTerminationPeriodsValues(this.currentData);
	}

	checkIfHasPools() {
		if (this.$form.querySelectorAll("[name=\"pool\"] option").length === 0) {
			setTimeout(() => {
				this.hide();
			}, 1000 * 2);
			const poolModal = peque.transactionSelectionModal.showModal("add-pool");
			peque.navigation
				.showError(trans("you_must_create_a_pool_first"))
				.then(() => {
					poolModal.$form.querySelector("[name=\"name\"]").focus();
				});
		}
	}

	isNew() {
		return this.getField("id").value.length === 0;
	}

	setupForm() {
		this.$date = this.$form.querySelector("[name=\"date\"]");
		this.$date.valueAsDate = new Date();

		this.$date.addEventListener("change", () => {
			if (typeof this.dateChangeTimeout !== "undefined") {
				clearTimeout(this.dateChangeTimeout);
			}
			this.dateChangeTimeout = setTimeout(() => {
				this.getLatestValuation();
			}, 1000);
		});

		this.$vestingType.addEventListener("change", () => {
			this.showVestingFields(this.$vestingType.value);
		});

		const $grantType = this.$form.querySelector("[name=\"grant-type\"]");
		$grantType.addEventListener("change", () => {
			this.showPresetFields($grantType.value);
		});
		this.showPresetFields($grantType.value);
	}

	showOrHideElements(show = false) {
		const $applyToAll = document.querySelector("[data-id=\"apply-to-all\"]");

		if (show) {
			$applyToAll.classList.remove("d-none");
		} else {
			$applyToAll.classList.add("d-none");
		}
	}

	showPresetFields(grantType) {
		grantType = parseInt(grantType);
		const isNew = this.isNew();

		const $strikePrice = this.$form
			.querySelector("[name=\"strike-price\"]")
			.closest(".form-group").parentElement;
		const $purchasePrice = this.$form
			.querySelector("[name=\"purchase-price\"]")
			.closest(".form-group").parentElement;

		const $ppsPriceLabel = this.$form.querySelector(
			"[data-id=\"pps-price-label\"]",
		);
		const $strikePriceLabel = this.$form.querySelector(
			"[data-id=\"strike-price-label\"]",
		);
		const $purchasePriceDefinition = this.$form.querySelector(
			"[data-id=\"purchase-price-definition\"]",
		);

		$strikePrice.classList.add("d-none");
		$purchasePrice.classList.add("d-none");
		$ppsPriceLabel.classList.add("d-none");
		$purchasePriceDefinition.classList.add("d-none");
		$strikePriceLabel.classList.remove("d-none");

		switch (grantType) {
			case peque.GRANT_TYPE_PHANTOM_SHARES:
				$purchasePrice.classList.remove("d-none");
				break;
			case peque.GRANT_TYPE_SARS:
				$strikePrice.classList.remove("d-none");
				$purchasePrice.classList.remove("d-none");
				$ppsPriceLabel.classList.remove("d-none");
				$ppsPriceLabel.classList.remove("d-none");
				$purchasePriceDefinition.classList.remove("d-none");
				$strikePriceLabel.classList.add("d-none");

				if (isNew) {
					this.getLatestValuation();
				}
				break;
			case peque.GRANT_TYPE_STOCK:
			case peque.GRANT_TYPE_WARRANTS:
			default:
				$strikePrice.classList.remove("d-none");
				$purchasePrice.classList.remove("d-none");
				break;
		}
	}

	getLatestValuation() {
		return new Promise((resolve, reject) => {
			peque
				.api(
					"valuations/latest",
					{
						company: this.companyId,
						simulation: $_GET("simulation"),
						date: this.$date.value,
					},
					"get",
				)
				.then((response) => {
					this.setFloat(
						"strike-price",
						response.result ? response.result.private.price : 0,
					);
					resolve();
				});
		});
	}

	showVestingFields(type) {
		type = parseInt(type);

		this.$form.querySelectorAll("[data-vesting-child]").forEach((el) => {
			switch (type) {
				case this.VESTING_TYPE_SIMPLE:
					el.classList.remove("d-none");
					break;
				default:
					el.classList.add("d-none");
					break;
			}
		});
	}

	handleTerminationPeriodsUnits() {
		let data = {};
		["voluntary", "involuntary", "with_cause", "death", "disability", "retirement"].forEach((type) => {
			const $fieldName = type.replace("_", "-");
			const $field = this.getField($fieldName);
			const $units = this.getField($fieldName + "-unit");

			if ($field.value === "") {
				data[type] = null
				console.log(type);
				return;
			}

			switch ($units.value) {
				case "months":
					data[type] = parseInt($field.value, 10) * 30 + 1;
					break;
				case "years":
					data[type] = parseInt($field.value, 10) * 365 + 1;
					break;
				default:
					data[type] = parseInt($field.value, 10) + 1;
					break;
			}
		});
		return data;
	}

	handleTerminationPeriodsValues(data) {
		if (!data) {
			return;
		}
		["voluntary", "involuntary", "with_cause", "death", "disability", "retirement"].forEach((field) => {
			const $fieldName = field.replace("_", "-");
			const $field = this.getField($fieldName);
			const $units = this.getField($fieldName + "-unit");

			if (data[field] === undefined || data[field] === null) {
				$field.value = "";
				return;
			}

			if (data[field] === 0) {
				$field.value = 0;
				$units.value = "days";
			} else if (data[field] % 365 === 0) {
				$field.value = data[field] / 365;
				$units.value = "years";
			} else if (data[field] % 30 === 0) {
				$field.value = data[field] / 30;
				$units.value = "months";
			} else {
				$field.value = data[field];
				$units.value = "days";
			}

		});
	}

	validateAndSendForm() {
		this.enableSubmitButton(false);

		this.getFields(this.formFields)
			.then((data) => {
				data.apply_to_all =
					document.getElementById("apply-to-all")?.checked || false;
				data.company = this.companyId;
				data.simulation =
					typeof window.pequeData.simulation === "undefined"
						? $_GET("simulation")
						: window.pequeData.simulation;
				data.type = 9;

				data = Object.assign(data, this.handleTerminationPeriodsUnits());

				peque
					.api("transactions", JSON.stringify(data), "post")
					.then((response) => {
						this.mutateReactTransactions();
					})
					.catch((error) => {
						this.enableSubmitButton(true);

						if (error.field) {
							this.showErrorOnField(
								error.result,
								this.$form.querySelector("[name=\"" + error.field + "\"]"),
							);
						}
					});
			})
			.catch((error) => {
				this.enableSubmitButton(true);

				if (typeof error.el === "undefined") {
					throw error;
				}
				this.showErrorOnField(error.message, error.el);
			});
	}
}
